import { Badge } from '@/components/ui/badge'
import InactiveWarning from '@/components/ui/inactive-warning'
import { BadgeAppearance } from '@/enum/global.enum'
import { useBadgeAppearance } from '@/hooks/use-badge-appearance'
import { cn } from '@/lib/utils'

interface ColourBadgeProps extends Omit<React.ComponentProps<'span'>, 'children'> {
  /** Label shown inside the badge. Renders the `emptyLabel` placeholder when blank. */
  name?: string | null
  /** Badge colour, typically the hex stored against the status/rating/priority record. */
  colour?: string | null
  /** Forces filled or outline, ignoring the organisation preference. Use sparingly. */
  appearance?: BadgeAppearance
  /** Shown instead of the badge when there is no name. */
  emptyLabel?: string
  /** When `false`, prefixes the badge with the shared inactive warning icon. */
  active?: boolean
  /** Tooltip for the inactive warning; required to show the warning. */
  inactiveLabel?: string
}

/**
 * Colour-coded badge for statuses, ratings and priorities.
 *
 * Filled or outline is decided by the organisation's badge appearance preference, so
 * every list, table and detail modal in the platform stays consistent. Pass `appearance`
 * only when a badge must ignore that preference.
 *
 * @example
 * <ColourBadge name={status.name} colour={status.colour} active={status.active} inactiveLabel='Status is inactive' />
 */
const ColourBadge = ({
  name,
  colour,
  appearance,
  emptyLabel = '-',
  active,
  inactiveLabel,
  className,
  style,
  ...props
}: ColourBadgeProps) => {
  const { appearance: organisationAppearance, getColourStyle } = useBadgeAppearance()

  if (!name) return <span className='text-muted-foreground'>{emptyLabel}</span>

  const resolvedAppearance = appearance ?? organisationAppearance
  const isFilled = resolvedAppearance === BadgeAppearance.FILLED

  const badge = (
    <Badge
      // A filled badge without a colour falls back to the muted secondary look rather than primary
      variant={isFilled ? (colour ? 'default' : 'secondary') : 'outline'}
      className={cn('capitalize text-nowrap font-light rounded-full', isFilled ? 'border-none' : undefined, className)}
      style={{ ...getColourStyle(colour, resolvedAppearance), ...style }}
      {...props}
    >
      {name}
    </Badge>
  )

  if (!inactiveLabel) return badge

  return (
    <span className='inline-flex items-center gap-2'>
      <InactiveWarning active={active} label={inactiveLabel} />
      {badge}
    </span>
  )
}

export { ColourBadge }
